import type {
  SecondaryNavigationActionControlConfig,
  SecondaryNavigationState,
  SecondaryNavigationTitleAccessoryControlConfig,
} from './secondaryNavigation.types';

const areSecondaryNavigationControlConfigsEqual = (
  previousControl: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig,
  nextControl: SecondaryNavigationActionControlConfig | SecondaryNavigationTitleAccessoryControlConfig
) => {
  if (previousControl.type !== nextControl.type || !Object.is(previousControl.key, nextControl.key)) {
    return false;
  }

  switch (previousControl.type) {
    case 'button':
      return nextControl.type === 'button'
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.endIcon, nextControl.endIcon)
        && Object.is(previousControl.errorLabel, nextControl.errorLabel)
        && Object.is(previousControl.isSaving, nextControl.isSaving)
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.saveFailed, nextControl.saveFailed)
        && Object.is(previousControl.savedLabel, nextControl.savedLabel)
        && Object.is(previousControl.savingLabel, nextControl.savingLabel)
        && Object.is(previousControl.startIcon, nextControl.startIcon)
        && Object.is(previousControl.variant, nextControl.variant);
    case 'iconButton':
      return nextControl.type === 'iconButton'
        && Object.is(previousControl.active, nextControl.active)
        && Object.is(previousControl.ariaLabel, nextControl.ariaLabel)
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.icon, nextControl.icon)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.variant, nextControl.variant);
    case 'infoLabel':
      return nextControl.type === 'infoLabel'
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.value, nextControl.value)
        && Object.is(previousControl.valueTone, nextControl.valueTone);
    case 'selector':
      return nextControl.type === 'selector'
        && Object.is(previousControl.disabled, nextControl.disabled)
        && Object.is(previousControl.endIcon, nextControl.endIcon)
        && Object.is(previousControl.label, nextControl.label)
        && Object.is(previousControl.onClick, nextControl.onClick)
        && Object.is(previousControl.positiveFeedbackDurationMs, nextControl.positiveFeedbackDurationMs)
        && Object.is(previousControl.positiveFeedbackKey, nextControl.positiveFeedbackKey)
        && Object.is(previousControl.startIcon, nextControl.startIcon)
        && Object.is(previousControl.value, nextControl.value)
        && Object.is(previousControl.valueTone, nextControl.valueTone)
        && Object.is(previousControl.variant, nextControl.variant);
    default:
      return false;
  }
};

const areSecondaryNavigationActionControlsEqual = (
  previousControls: SecondaryNavigationActionControlConfig[] = [],
  nextControls: SecondaryNavigationActionControlConfig[] = []
) => {
  if (previousControls.length !== nextControls.length) {
    return false;
  }

  return previousControls.every((previousControl, index) => {
    const nextControl = nextControls[index];

    return Boolean(nextControl) && areSecondaryNavigationControlConfigsEqual(previousControl, nextControl);
  });
};

const areSecondaryNavigationTitleAccessoryControlsEqual = (
  previousControl?: SecondaryNavigationTitleAccessoryControlConfig,
  nextControl?: SecondaryNavigationTitleAccessoryControlConfig
) => {
  if (!previousControl || !nextControl) {
    return previousControl === nextControl;
  }

  return areSecondaryNavigationControlConfigsEqual(previousControl, nextControl);
};

const areSecondaryNavigationSkeletonPropsEqual = (
  previousProps?: SecondaryNavigationState['skeletonProps'],
  nextProps?: SecondaryNavigationState['skeletonProps']
) => {
  if (!previousProps || !nextProps) {
    return previousProps === nextProps;
  }

  return Object.is(previousProps.actionCount, nextProps.actionCount)
    && Object.is(previousProps.itemCount, nextProps.itemCount)
    && Object.is(previousProps.showActions, nextProps.showActions)
    && Object.is(previousProps.showStatus, nextProps.showStatus)
    && Object.is(previousProps.showTitleAccessory, nextProps.showTitleAccessory);
};

export const areSecondaryNavigationStatesEqual = (
  previousState: SecondaryNavigationState | null,
  nextState: SecondaryNavigationState | null
) => {
  if (previousState === nextState) {
    return true;
  }

  if (!previousState || !nextState) {
    return false;
  }

  if (
    !areSecondaryNavigationActionControlsEqual(previousState.actions, nextState.actions) ||
    !Object.is(previousState.actionsNode, nextState.actionsNode) ||
    !Object.is(previousState.ariaLabel, nextState.ariaLabel) ||
    !Object.is(previousState.isLoading, nextState.isLoading) ||
    !areSecondaryNavigationSkeletonPropsEqual(previousState.skeletonProps, nextState.skeletonProps) ||
    !Object.is(previousState.status, nextState.status) ||
    !Object.is(previousState.title, nextState.title) ||
    !areSecondaryNavigationTitleAccessoryControlsEqual(previousState.titleAccessory, nextState.titleAccessory) ||
    !Object.is(previousState.titleAccessoryNode, nextState.titleAccessoryNode) ||
    !Object.is(previousState.titleLeading, nextState.titleLeading) ||
    !Object.is(previousState.titleTooltip, nextState.titleTooltip) ||
    // Pages that override URL-based active matching (e.g. Intelligence) may change ONLY
    // value between tab navigations — skipping it here freezes the highlighted tab.
    !Object.is(previousState.value, nextState.value)
  ) {
    return false;
  }

  const previousItems = previousState.items ?? [];
  const nextItems = nextState.items ?? [];

  if (previousItems.length !== nextItems.length) {
    return false;
  }

  return previousItems.every((previousItem, index) => {
    const nextItem = nextItems[index];

    if (!nextItem) {
      return false;
    }

    return Object.is(previousItem.disabled, nextItem.disabled)
      && Object.is(previousItem.label, nextItem.label)
      && Object.is(previousItem.to, nextItem.to)
      && Object.is(previousItem.value, nextItem.value);
  });
};
